/**
 * WorkflowBottlenecksPanel - Painel de gargalos dos processos (Fase 8)
 *
 * Mostra o tempo médio que cada etapa leva para ser concluída e quantos
 * processos estão parados há mais de 7 dias. Leitura por RPC segura
 * (`get_workflow_bottlenecks`), sem consulta direta a tabelas.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-06 v1.2.0 - Coluna "Workflow" (quando o painel não está preso a um
 *          processo) e etapa clicável: abre o construtor do workflow já com a etapa
 *          selecionada (?node=<id>), melhorando a auditoria de gargalos.
 * RELEASE: 2026-10-06 v1.1.0 - Padrão visual dos painéis (PREMIUM_DASHBOARD_CARD), tabela
 *          com cabeçalho, duração legível (dias/horas/min), barra relativa,
 *          filtro de processo e selo de parados clicável (abre Execuções).
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (Fase 8 do Workflow 2.0)
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Gauge, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useWorkflowBottlenecks } from '@/hooks/useWorkflowDesigner';
import { PREMIUM_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';
import { WorkflowDefinitionFilter } from '@/components/workflow/WorkflowDefinitionFilter';
import { cn } from '@/lib/utils';

interface Props {
  /** Quando informado, restringe a análise a um único processo (sem filtro visível) */
  definitionId?: string | null;
}

/** Converte minutos em texto legível: "11d 12h", "2h 15min", "menos de 1 min" */
function formatarDuracao(min: number | null): string {
  if (min === null || min === undefined) return '—';
  if (min < 1) return 'menos de 1 min';
  const total = Math.round(min);
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`;
  return `${m} min`;
}

export function WorkflowBottlenecksPanel({ definitionId = null }: Props) {
  const navigate = useNavigate();
  // Filtro local só é exibido quando o painel não vem preso a um processo
  const [filtro, setFiltro] = useState<string | null>(null);
  const alvo = definitionId ?? filtro;
  const { data, isLoading } = useWorkflowBottlenecks(alvo, 30);

  // Ordena: etapas relevantes por tempo médio; tempo zero sem aberto vão ao fim
  const nodes = useMemo(() => {
    const lista = [...(data?.nodes ?? [])];
    const irrelevante = (n: (typeof lista)[number]) => !n.em_aberto && !(n.minutos_medio ?? 0);
    return lista.sort((a, b) => {
      if (irrelevante(a) !== irrelevante(b)) return irrelevante(a) ? 1 : -1;
      return (b.minutos_medio ?? 0) - (a.minutos_medio ?? 0);
    });
  }, [data?.nodes]);
  const maior = Math.max(1, ...nodes.map((n) => n.minutos_medio ?? 0));
  const parados = data?.instancias_paradas ?? 0;

  return (
    <div className={cn(PREMIUM_DASHBOARD_CARD, 'rounded-xl border bg-card')}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Gauge className="h-4 w-4" /> Gargalos dos processos (últimos 30 dias)
          </h3>
          <p className="text-xs text-muted-foreground">
            Etapas onde os processos ficam mais tempo até serem concluídas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {parados > 0 && (
            <Badge
              variant="outline"
              className="cursor-pointer gap-1 text-warning"
              onClick={() => navigate('/tenant/workflow/executions?status=em_andamento')}
              title="Ver execuções em andamento"
            >
              <AlertTriangle className="h-3 w-3" /> {parados} parado(s) há mais de 7 dias
            </Badge>
          )}
          {definitionId === null && <WorkflowDefinitionFilter value={filtro} onChange={setFiltro} />}
        </div>
      </div>

      <div className="px-4 pb-3">
        {isLoading ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando indicadores...
          </div>
        ) : nodes.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">
            Ainda não há execuções suficientes para medir gargalos.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 font-medium">Etapa</th>
                {/* Coluna "Workflow" só aparece na visão geral (painel preso a um processo é redundante) */}
                {definitionId === null && <th className="py-2 font-medium">Workflow</th>}
                <th className="w-24 py-2 text-right font-medium">Execuções</th>
                <th className="w-24 py-2 text-right font-medium">Em aberto</th>
                <th className="w-48 py-2 text-right font-medium">Tempo médio</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {nodes.slice(0, 8).map((n, i) => {
                const apagado = !n.em_aberto && !(n.minutos_medio ?? 0);
                const pct = ((n.minutos_medio ?? 0) / maior) * 100;
                return (
                  <tr key={n.node_id} className={cn(apagado && 'text-muted-foreground opacity-60')}>
                    <td className="py-2">
                      {/* Clique abre o construtor do workflow já com a etapa selecionada */}
                      <button
                        type="button"
                        className={cn(
                          'truncate text-left hover:underline',
                          i === 0 && !apagado && 'font-semibold text-destructive',
                        )}
                        title={`Abrir o workflow "${n.definition_name}" nesta etapa`}
                        onClick={() =>
                          navigate(`/tenant/workflow/${n.definition_id}?node=${n.node_id}`)
                        }
                      >
                        {n.label}
                      </button>
                    </td>
                    {definitionId === null && (
                      <td className="py-2">
                        <span className="block max-w-40 truncate text-muted-foreground" title={n.definition_name}>
                          {n.definition_name}
                        </span>
                      </td>
                    )}
                    <td className="py-2 text-right tabular-nums">{n.execucoes}</td>
                    <td className="py-2 text-right tabular-nums">{n.em_aberto}</td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-2">
                        <div className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-muted sm:block">
                          <div
                            className={cn('h-full rounded-full', i === 0 ? 'bg-destructive' : 'bg-primary/60')}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-24 text-right font-medium tabular-nums">
                          {formatarDuracao(n.minutos_medio)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
